/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Target,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  Users,
  Calendar,
  Star
} from 'lucide-react';
import { Button, SearchSelect } from '../../../components/shared';
import DataTable from '../../../components/shared/DataTable';
import CreateSubscriptionModal from '../../../components/admin/CreateSubscriptionModal';
import { useToastContext } from "../../../contexts/ToastContext";
import { StatsCard, StatsGrid } from '../../../components/shared';

import { 
  useSubscriptions, 
  useSubscriptionStats,
  useCreateSubscription, 
  useUpdateSubscription, 
  useDeleteSubscription, 
  useToggleSubscriptionStatus
} from '../../../services/subscriptionService';

const AdminSubscriptions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const { success, error: showError } = useToastContext();

  // API query parameters
  const queryParams = {
    is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
    sort_by: 'sort_order',
    sort_order: 'asc'
  };

  // Fetch subscriptions from API
  const { 
    data: subscriptionsData, 
    isLoading: loading, 
    error: subscriptionsError,
    refetch 
  } = useSubscriptions(queryParams);

  // Fetch subscription statistics
  const { 
    data: statsData, 
    isLoading: statsLoading, 
    error: statsError,
    refetch: refetchStats
  } = useSubscriptionStats();

  // Extract subscriptions from API response
  const subscriptions = subscriptionsData?.data || [];

  // Calculate statistics
  const totalSubscriptions = statsData?.data?.total_subscriptions || 0;
  const activeSubscriptions = statsData?.data?.active_subscriptions || 0;
  const inactiveSubscriptions = statsData?.data?.inactive_subscriptions || 0;

  // API mutations
  const createSubscriptionMutation = useCreateSubscription({
    onSuccess: () => {
      success('Subscription created successfully', 'success');
      setShowCreateModal(false);
      refetch(); // Refetch subscriptions data
      refetchStats(); // Refetch statistics
    },
    onError: (error) => {
      showError(error.message || 'Failed to create subscription', 'error');
    }
  });

  const updateSubscriptionMutation = useUpdateSubscription({
    onSuccess: () => {
      success('Subscription updated successfully', 'success');
      setShowEditModal(false);
      setSelectedSubscription(null);
      refetch(); // Refetch subscriptions data
      refetchStats(); // Refetch statistics
    },
    onError: (error) => {
      showError(error.message || 'Failed to update subscription', 'error');
    }
  });

  const deleteSubscriptionMutation = useDeleteSubscription({
    onSuccess: () => {
      success('Subscription deleted successfully', 'success');
      refetch(); // Refetch subscriptions data
      refetchStats(); // Refetch statistics
    },
    onError: (error) => {
      showError(error.message || 'Failed to delete subscription', 'error');
    }
  });

  const toggleStatusMutation = useToggleSubscriptionStatus({
    onSuccess: (data) => {
      // console.log('Toggle success:', data);
      success('Subscription status updated successfully', 'success');
      refetch(); // Refetch subscriptions data
      refetchStats(); // Refetch statistics
    },
    onError: (error) => {
      console.error('Toggle error:', error);
      showError(error.response?.data?.message || error.message || 'Failed to update subscription status', 'error');
    }
  });

  // Client-side filtering for search
  const filteredSubscriptions = subscriptions.filter(subscription => {
    const matchesSearch = subscription.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subscription.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Define columns for DataTable
  const columns = [
    {
      key: 'subscription',
      label: 'Subscription',
      render: (subscription) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center">
              <Target className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {subscription.name}
            </div>
            <div className="text-sm text-gray-500">
              {subscription.description}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'price',
      label: 'Price',
      render: (subscription) => (
        <div className="text-sm">
          <div className="font-medium text-gray-900">
            {subscription.currency} {subscription.price}
          </div>
          <div className="text-gray-500">
            per {subscription.duration_days} days
          </div>
        </div>
      )
    },
    {
      key: 'features',
      label: 'Features',
      render: (subscription) => (
        <div className="text-sm">
          <div className="text-gray-900">
            {subscription.max_bookings_per_month ? 
              `${subscription.max_bookings_per_month} bookings/month` : 
              'Unlimited bookings'
            }
          </div>
          <div className="text-gray-500">
            {subscription.max_services ? 
              `${subscription.max_services} services` : 
              'Unlimited services'
            }
          </div>
        </div>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (subscription) => (
        <div className="flex items-center">
          {subscription.is_active ? (
            <CheckCircle className="w-4 h-4 text-green-500" />
          ) : (
            <XCircle className="w-4 h-4 text-red-500" />
          )}
          <span className="ml-2">
            {subscription.is_active ? (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Active
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Inactive
              </span>
            )}
          </span>
        </div>
      )
    }
  ];

  // Define actions for DataTable
  const actions = [
    {
      label: 'Toggle',
      icon: CheckCircle,
      onClick: (subscription) => {
        // console.log('Toggling subscription:', subscription.id, 'Current status:', subscription.is_active);
        toggleStatusMutation.mutate({ 
          id: subscription.id, 
          isActive: !subscription.is_active 
        });
      },
      title: 'Toggle Status',
      variant: 'default'
    },
    {
      label: 'Edit',
      icon: Edit,
      onClick: (subscription) => openEditModal(subscription),
      title: 'Edit Subscription',
      variant: 'default'
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: (subscription) => handleDeleteSubscription(subscription.id),
      title: 'Delete Subscription',
      variant: 'default'
    }
  ];

  const handleCreateSubscription = (subscriptionData) => {
    createSubscriptionMutation.mutate(subscriptionData);
  };

  const handleEditSubscription = (subscriptionData) => {
    updateSubscriptionMutation.mutate({
      id: selectedSubscription.id,
      ...subscriptionData
    });
  };

  const handleDeleteSubscription = (subscriptionId) => {
    if (window.confirm('Are you sure you want to delete this subscription?')) {
      deleteSubscriptionMutation.mutate(subscriptionId);
    }
  };

  const openEditModal = (subscription) => {
    setSelectedSubscription(subscription);
    setShowEditModal(true);
  };

  // Handle API errors
  if (subscriptionsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Error Loading Subscriptions</h3>
          <p className="mt-1 text-sm text-gray-500">
            {subscriptionsError.message || 'Failed to load subscriptions'}
          </p>
          <div className="mt-6">
            <Button onClick={() => refetch()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading || statsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscriptions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Subscriptions</h1>
            <p className="mt-2 text-gray-600">Manage subscription plans for vendors</p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Subscription
            </Button>
          </div>
        </div>

        {/* Statistics */}
        <div className="mb-8">
          <StatsGrid 
            stats={[
              {
                key: 'total',
                title: 'Total Subscriptions',
                value: totalSubscriptions,
                icon: Target,
                variant: 'default'
              },
              {
                key: 'active',
                title: 'Active',
                value: activeSubscriptions,
                icon: CheckCircle,
                variant: 'success'
              },
              {
                key: 'inactive',
                title: 'Inactive',
                value: inactiveSubscriptions,
                icon: XCircle,
                variant: 'danger'
              }
            ]}
            columns={3}
          />
        </div>

        {/* Filters */}
       
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
              <div className="w-full sm:w-52">
              <SearchSelect
                label="Status Filter"
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
                placeholder="Filter by status"
              />
              </div>

              
            </div>

            
          </div>
        </div>

        {/* Subscriptions Table */}
        <DataTable
          data={filteredSubscriptions}
          columns={columns}
          actions={actions}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search subscriptions..."
          emptyState={{
            icon: AlertCircle,
            title: "No subscriptions found",
            description: searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by creating a new subscription plan.',
            action: (!searchTerm && statusFilter === 'all') ? (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Subscription
              </Button>
            ) : null
          }}
        />

        {/* Create Modal */}
        {showCreateModal && (
          <CreateSubscriptionModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSave={handleCreateSubscription}
            isLoading={createSubscriptionMutation.isLoading}
          />
        )}

        {/* Edit Modal */}
        {showEditModal && selectedSubscription && (
          <CreateSubscriptionModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedSubscription(null);
            }}
            onSave={handleEditSubscription}
            subscription={selectedSubscription}
            isEdit={true}
            isLoading={updateSubscriptionMutation.isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default AdminSubscriptions;
